export type ClarityTagValue = string | number | boolean | readonly string[] | null | undefined;
export type ClarityStorageConsent = 'granted' | 'denied';

export interface ClarityConsent {
    readonly adStorage?: ClarityStorageConsent;
    readonly analyticsStorage?: ClarityStorageConsent;
}

export interface ClarityInitOptions {
    readonly projectId?: string | null;
    readonly enabled?: boolean;
    readonly consent?: ClarityConsent;
    readonly identifyId?: string | null;
    readonly identifyPageId?: string | null;
    readonly tags?: Readonly<Record<string, ClarityTagValue>>;
}

export interface ClarityIdentifyOptions {
    readonly sessionId?: string | null;
    readonly pageId?: string | null;
    readonly friendlyName?: string | null;
}

interface ClarityConsentCommandPayload {
    readonly ad_Storage: ClarityStorageConsent;
    readonly analytics_Storage: ClarityStorageConsent;
}

type ClarityNormalizedTagValue = string | readonly string[];

type ClarityCommand =
    | ['consentv2', ClarityConsentCommandPayload]
    | ['identify', string, (string | undefined)?, (string | undefined)?, (string | undefined)?]
    | ['set', string, ClarityNormalizedTagValue]
    | ['event', string]
    | ['upgrade', string];

type ClarityFunction = ((...args: ClarityCommand) => void) & {
    q?: ClarityCommand[];
};

declare global {
    interface Window {
        clarity?: ClarityFunction;
    }
}

// The Clarity script is bound to one project ID for the page lifetime.
let activeProjectId: string | null = null;
let commandsEnabled = false;

export function initializeClarity({
    projectId,
    enabled = true,
    consent,
    identifyId,
    identifyPageId,
    tags,
}: ClarityInitOptions): boolean {
    const trimmedProjectId = projectId?.trim();

    if (!enabled || !trimmedProjectId || typeof window === 'undefined' || typeof document === 'undefined') {
        return false;
    }

    commandsEnabled = true;

    const clarity = ensureClarityFunction();
    if (!clarity) {
        commandsEnabled = false;
        return false;
    }

    if (!activeProjectId) {
        activeProjectId = trimmedProjectId;
        injectClarityScript(trimmedProjectId);
    } else if (activeProjectId !== trimmedProjectId) {
        commandsEnabled = false;
        return false;
    }

    if (consent) {
        setClarityConsent(consent);
    }

    identifyClarity(identifyId, { pageId: identifyPageId });
    setClarityTags(tags);

    return true;
}

export function disableClarity(): void {
    commandsEnabled = false;

    if (!activeProjectId) {
        return;
    }

    sendClarityCommandUnsafe('consentv2', {
        ad_Storage: 'denied',
        analytics_Storage: 'denied',
    });
}

export function setClarityConsent({ adStorage = 'denied', analyticsStorage = 'denied' }: ClarityConsent): void {
    sendClarityCommand('consentv2', {
        ad_Storage: adStorage,
        analytics_Storage: analyticsStorage,
    });
}

export function identifyClarity(userId: string | null | undefined, options: ClarityIdentifyOptions = {}): void {
    const trimmedUserId = normalizeOptionalString(userId);

    if (!trimmedUserId) {
        return;
    }

    sendClarityCommand(
        'identify',
        trimmedUserId,
        normalizeOptionalString(options.sessionId),
        normalizeOptionalString(options.pageId),
        normalizeOptionalString(options.friendlyName),
    );
}

export function setClarityTags(tags: Readonly<Record<string, ClarityTagValue>> | null | undefined): void {
    if (!tags) {
        return;
    }

    for (const [key, value] of Object.entries(tags)) {
        setClarityTag(key, value);
    }
}

export function setClarityTag(key: string, value: ClarityTagValue): void {
    const normalizedValue = normalizeTagValue(value);

    if (!key || normalizedValue == null) {
        return;
    }

    sendClarityCommand('set', key, normalizedValue);
}

export function trackClarityEvent(name: string): void {
    if (!name) {
        return;
    }

    sendClarityCommand('event', name);
}

export function upgradeClaritySession(reason: string): void {
    if (!reason) {
        return;
    }

    sendClarityCommand('upgrade', reason);
}

function ensureClarityFunction(): ClarityFunction | null {
    if (typeof window === 'undefined') {
        return null;
    }

    if (!window.clarity) {
        const clarity: ClarityFunction = (...args: ClarityCommand) => {
            clarity.q = clarity.q ?? [];
            clarity.q.push(args);
        };

        window.clarity = clarity;
    }

    return window.clarity;
}

function getClarityFunction(): ClarityFunction | null {
    return typeof window === 'undefined' ? null : (window.clarity ?? null);
}

function sendClarityCommand(...args: ClarityCommand): void {
    if (!commandsEnabled || !activeProjectId) {
        return;
    }

    sendClarityCommandUnsafe(...args);
}

function sendClarityCommandUnsafe(...args: ClarityCommand): void {
    getClarityFunction()?.(...args);
}

function injectClarityScript(projectId: string): void {
    const scriptId = `clarity-${projectId}`;

    if (document.getElementById(scriptId)) {
        return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${encodeURIComponent(projectId)}`;

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
    } else {
        document.head.appendChild(script);
    }
}

function normalizeTagValue(value: ClarityTagValue): ClarityNormalizedTagValue | null {
    if (value == null) {
        return null;
    }

    if (Array.isArray(value)) {
        const values = value.map(item => item.trim()).filter(Boolean);
        return values.length > 0 ? values : null;
    }

    const stringValue = String(value).trim();
    return stringValue || null;
}

function normalizeOptionalString(value: string | null | undefined): string | undefined {
    return value?.trim() || undefined;
}
